import React, { useCallback, useEffect, useState } from "react";
import { BookOpen, FileText, Search } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton, AppModal, Callout, Input, SegmentedControl, cn } from "../ui";
import Shimmer from "../Common/Shimmer";
import PassageForm, { emptyPassage, passagePayload, passageProblem } from "./PassageForm";

/**
 * Choose the passage a question is about, or write a new one, without leaving
 * the question editor (and the half-written question in it).
 *
 * There is deliberately no <form> here: this dialog is portalled out of the
 * editor's DOM but not out of its React tree, and a submit inside it would
 * bubble to the editor's onSubmit and save the QUESTION.
 *
 * Props: show, onHide, onPick({ _id, title, kind }), categories, defaultCategory
 */
export default function PassagePickerModal({ show, onHide, onPick, categories, defaultCategory = "" }) {
  const [mode, setMode] = useState("choose");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(emptyPassage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setMode("choose");
    setQuery("");
    setSearch("");
    setError("");
    setDraft({ ...emptyPassage(), category: defaultCategory || "" });
  }, [show, defaultCategory]);

  useEffect(() => {
    const id = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/quiz-passages", { params: { limit: 50, search: search || undefined } });
      setRows(data.passages || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (show && mode === "choose") load();
  }, [show, mode, load]);

  const create = async () => {
    const problem = passageProblem(draft);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError("");
    try {
      const { data } = await apiClient.post("/quiz-passages", passagePayload(draft));
      onPick(data.passage);
    } catch (err) {
      setError(err.response?.data?.message || "Could not save the passage.");
    } finally {
      setSaving(false);
    }
  };

  const uploadedKey = draft.file?.key;
  const loadDraftFile = uploadedKey
    ? async () => {
      const { data } = await apiClient.get("/quiz-passages/upload-link", { params: { key: uploadedKey } });
      return data?.url || null;
    }
    : null;

  return (
    <AppModal
      show={show}
      onHide={onHide}
      size="lg"
      title="Passage for this question"
      icon={<BookOpen size={18} aria-hidden="true" />}
      footer={mode === "new" ? (
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton type="button" variant="ghost" onClick={onHide}>Cancel</AppButton>
          <AppButton type="button" onClick={create} disabled={saving}>
            {saving ? "Saving…" : "Save and use this passage"}
          </AppButton>
        </div>
      ) : null}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <SegmentedControl
          label="Choose or create"
          value={mode}
          onChange={setMode}
          options={[
            { value: "choose", label: "Choose a passage" },
            { value: "new", label: "New passage" },
          ]}
        />

        {mode === "choose" ? (
          <>
            <div className="tw-relative">
              <Search size={16} className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-content-subtle" aria-hidden="true" />
              <Input
                aria-label="Search passages"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title or words in the passage…"
                className="tw-pl-9"
              />
            </div>
            {loading ? (
              <Shimmer height={56} count={3} />
            ) : rows.length === 0 ? (
              <p className="tw-m-0 tw-py-4 tw-text-center tw-text-sm tw-text-content-muted">
                {search ? "No passage matches." : "No passages yet."}{" "}
                <button
                  type="button"
                  className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 [font:inherit] tw-font-semibold tw-text-primary tw-underline tw-focus-ring"
                  onClick={() => setMode("new")}
                >
                  Write a new one
                </button>
              </p>
            ) : (
              <ul className="tw-m-0 tw-flex tw-max-h-[50vh] tw-list-none tw-flex-col tw-gap-1.5 tw-overflow-y-auto tw-p-0">
                {rows.map((p) => (
                  <li key={p._id}>
                    <button
                      type="button"
                      onClick={() => onPick(p)}
                      className={cn(
                        "tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3 tw-py-2.5 tw-text-left",
                        "tw-transition-colors hover:tw-border-primary/50 hover:tw-bg-surface-hover tw-focus-ring",
                      )}
                    >
                      {p.kind === "pdf"
                        ? <FileText size={16} className="tw-mt-0.5 tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
                        : <BookOpen size={16} className="tw-mt-0.5 tw-shrink-0 tw-text-content-muted" aria-hidden="true" />}
                      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                        <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{p.title}</span>
                        <span className="tw-text-xs tw-text-content-muted">
                          {p.kind === "pdf" ? `Document · ${p.file?.name || "PDF"}` : "Passage"}
                          {" · "}{p.questionCount || 0} question{p.questionCount === 1 ? "" : "s"}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            {error && <Callout tone="danger">{error}</Callout>}
            <PassageForm value={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} categories={categories} loadFileUrl={loadDraftFile} />
          </>
        )}
      </div>
    </AppModal>
  );
}
